import React, { useEffect, useState } from 'react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { useNavigate } from 'react-router-dom';
import {
  MATERIAL_LIST_ENDPOINT,
  GET_LOADING_POINTS_ENDPOINT,
  SEARCH_PRODUCTION_ENDPOINT
} from '../../config/api';
import { decryptData } from '../../utils/encrypt';
import ProductionSummaryCard from '../../components/production/cards/ProductionSummaryCard';

function ProductionSearch() {
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState([]);
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({
    material_id: '',
    packing_type: '',
    plant_name: '',
    from_date: '',
    to_date: ''
  });

  const packingOptions = ['25 Kgs', '50 Kgs', 'Loose Bulker'];
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('suyog_user');
    if (stored) {
      const parsed = decryptData(stored);
      setUserId(parsed?.user_id || null);
      setOrgId(parsed?.selected_org?.value || null);
    }
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchMaterials();
      fetchLoadingPoints();
    }
  }, [orgId]);

  const fetchMaterials = async () => {
    try {
      const res = await fetch(MATERIAL_LIST_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (Array.isArray(data)) setMaterials(data);
    } catch (err) {
      console.error('Material fetch error:', err);
    }
  };

  const fetchLoadingPoints = async () => {
    try {
      const res = await fetch(GET_LOADING_POINTS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId })
      });
      const data = await res.json();
      if (res.ok && data.success) setLoadingPoints(data.records);
    } catch (err) {
      console.error('Loading point fetch error:', err);
    }
  };

  const handleSearch = async () => {
    if (!orgId) return;

    const payload = { user_id: userId, org_id: orgId, ...filters };

    try {
      const res = await fetch(SEARCH_PRODUCTION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(data.records);
      } else {
        alert('No records found.');
        setRecords([]);
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleNewEntry = () => {
    navigate('/production/new');
  };

  const getSummaryMap = (key) => {
    const map = {};
    for (const r of records) {
      const label = r[key] || 'Unknown';
      const qty = parseFloat(r.quantity || 0);
      if (!map[label]) map[label] = { count: 0, qty: 0 };
      map[label].count += 1;
      map[label].qty += qty;
    }
    return map;
  };

  const materialSummary = getSummaryMap('material_name');
  const plantSummary = getSummaryMap('plant_name');
  const packingSummary = getSummaryMap('packing_type');

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Production Search</h2>
          <button className="btn btn-success" onClick={handleNewEntry}>+ New Entry</button>
        </div>

        {/* Filter Form */}
        <div className="card p-3 shadow-sm rounded-4 mb-4">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Material</label>
              <select className="form-select" name="material_id" value={filters.material_id} onChange={handleInputChange}>
                <option value="">-- All Materials --</option>
                {materials.map(mat => (
                  <option key={mat.id} value={mat.id}>{mat.material_name}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Packing Type</label>
              <select className="form-select" name="packing_type" value={filters.packing_type} onChange={handleInputChange}>
                <option value="">-- All Types --</option>
                {packingOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Loading Point</label>
              <select className="form-select" name="plant_name" value={filters.plant_name} onChange={handleInputChange}>
                <option value="">-- All Points --</option>
                {loadingPoints.map(lp => (
                  <option key={lp.id} value={lp.name}>{lp.name}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Date From</label>
              <input type="date" className="form-control" name="from_date" value={filters.from_date} onChange={handleInputChange} />
            </div>

            <div className="col-md-3">
              <label className="form-label">Date To</label>
              <input type="date" className="form-control" name="to_date" value={filters.to_date} onChange={handleInputChange} />
            </div>

            <div className="col-md-3 align-self-end">
              <button className="btn btn-primary" onClick={handleSearch}>Search</button>
            </div>
          </div>
        </div>

        {records.length > 0 && (
  <>
    {/* Table for larger screens */}
    <div className="d-none d-md-block table-responsive">
      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Material</th>
            <th>Packing</th>
            <th>Loading Point</th>
            <th>Quantity</th>
            <th>Production Date</th>
          </tr>
        </thead>
        <tbody>
          {records.map((row, index) => (
            <tr key={row.id || index}>
              <td>{index + 1}</td>
              <td>{row.material_name}</td>
              <td>{row.packing_type}</td>
              <td>{row.plant_name}</td>
              <td>{row.quantity}</td>
              <td>{row.production_date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Cards for small screens */}
    <div className="d-md-none">
      {records.map((row, index) => (
        <div className="card mb-3 shadow-sm" key={row.id || index}>
          <div className="card-body p-3">
            <h6 className="fw-bold mb-2">#{index + 1}</h6>
            <p className="mb-1"><strong>Material:</strong> {row.material_name}</p>
            <p className="mb-1"><strong>Packing:</strong> {row.packing_type}</p>
            <p className="mb-1"><strong>Loading Point:</strong> {row.plant_name}</p>
            <p className="mb-1"><strong>Quantity:</strong> {row.quantity}</p>
            <p className="mb-0"><strong>Date:</strong> {row.production_date}</p>
          </div>
        </div>
      ))}
    </div>
  </>
)}

        {/* Combined Summary Card */}
        {records.length > 0 && (
          <div className="card shadow-sm rounded-4 p-3 mt-4">
            <h5 className="fw-bold text-primary mb-4">Production Summary</h5>
            <div className="row g-4">
              <div className="col-md-4">
                <ProductionSummaryCard title="Material-wise Summary" data={materialSummary} />
              </div>
              <div className="col-md-4">
                <ProductionSummaryCard title="Loading Point-wise Summary" data={plantSummary} />
              </div>
              <div className="col-md-4">
                <ProductionSummaryCard title="Packing Type-wise Summary" data={packingSummary} />
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default ProductionSearch;
